import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RouteKeys } from 'src/app/share/helper/route-keys.helper';

// Zentrale Zeitzone für den Scheduler
export const kalenderSchedulerTimeZone = 'Europe/Berlin';

// Request-Modell für das Laden der Termine eines sichtbaren Bereichs
export interface KalenderRequestViewModel {
  from: string;
  to: string;
}

// Antwortmodell des Backends
// Start und Ende kommen hier als String zurück
export interface KalenderResponseViewModel {
  id: string;
  text: string;
  description: string | null;
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
}

export type KalenderCreateTerminViewModel = Omit<KalenderResponseViewModel, 'id'>;
export type KalenderUpdateTerminViewModel = KalenderResponseViewModel;
export type KalenderDeleteTerminViewModel = Pick<KalenderResponseViewModel, 'id'>;

// Scheduler-internes Modell für die Anzeige 
// Start und Ende hier als echte `Date`-Werte
export interface SchedulerTermin {
  id: string;
  text: string;
  description: string | null;
  startDate: Date;
  endDate: Date;
  allDay: boolean;
  location: string | null;
}

// Einheitliche Uhrzeit für Termin Popup
export interface KalenderTimedRange {
  startDate: Date;
  endDate: Date;
}

// Start und Ende werden von String in `Date` umgewandelt fürs Frontend
export function mapKalenderResponseToSchedulerTermin(a: KalenderResponseViewModel): SchedulerTermin {
  return {
    ...a,
    startDate: new Date(a.startDate),
    endDate: new Date(a.endDate)
  };
}

// Hilfsfunktion für alle Stellen, die ein Datum lesen:
// - DevExtreme kann `Date` liefern durch Serialisierung können aber auch Strings ankommen
// - ungültige Werte sollen als `null` enden
export function toValidKalenderDate(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Liest einen vollständigen Uhrzeitbereich aus zwei Werten
// und verwirft unvollständige oder logisch falsche Bereiche.
export function toKalenderTimedRange(
  startValue: string | Date | null | undefined,
  endValue: string | Date | null | undefined
): KalenderTimedRange | null {
  const startDate = toValidKalenderDate(startValue);
  const endDate = toValidKalenderDate(endValue);

  if (!startDate || !endDate || endDate < startDate) {
    return null;
  }

  return { startDate, endDate };
}

// Aus einem `Date` wieder den API-String bauen fürs Backend
//
// Ablauf:
// 1. Eingabe in ein `Date` überführen
// 2. Einzelteile gezielt in der Kalender-Zeitzone lesen
// 3. Daraus einen String `yyyy-MM-ddTHH:mm:ss` zusammensetzen
//
// `forceMidnight` wird für Ganztag genutzt, um Uhrzeiten absichtlich auf `00:00:00` zu setzen
export function toApiDateInKalenderTimeZone(value: string | Date, forceMidnight = false): string {
  const d = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: kalenderSchedulerTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(d);

  const get = (type: string) => parts.find((x) => x.type === type)?.value ?? '00';
  const yyyy = get('year');
  const mm = get('month');
  const dd = get('day');
  const hh = forceMidnight ? '00' : get('hour');
  const mi = forceMidnight ? '00' : get('minute');
  const ss = forceMidnight ? '00' : get('second');

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
}

@Injectable({ providedIn: 'root' })
export class KalenderService {
  constructor(private readonly http: HttpClient) { }
  // Backend-Antworten sofort in das lokale Scheduler-Modell überführen

  getMeineTermine(payload: KalenderRequestViewModel): Observable<SchedulerTermin[]> {
    return this.http.post<KalenderResponseViewModel[]>(RouteKeys.Kalender.get_termine, payload).pipe(
      map(items => (items ?? []).map(mapKalenderResponseToSchedulerTermin))
    );
  }

  addTermin(payload: KalenderCreateTerminViewModel): Observable<SchedulerTermin> {
    return this.http.post<KalenderResponseViewModel>(RouteKeys.Kalender.add_termin, payload).pipe(
      map(mapKalenderResponseToSchedulerTermin)
    );
  }

  updateTermin(payload: KalenderResponseViewModel): Observable<SchedulerTermin> {
    return this.http.post<KalenderResponseViewModel>(RouteKeys.Kalender.update_termin, payload).pipe(
      map(mapKalenderResponseToSchedulerTermin)
    );
  }

  deleteTermin(payload: KalenderDeleteTerminViewModel): Observable<void> {
    return this.http.post<void>(RouteKeys.Kalender.delete_termin, payload);
  }
}
